import { marked } from "marked"
import { useEffect, useLayoutEffect } from "react"
import { Helmet } from "react-helmet-async"
import { useLocation } from "react-router-dom"

import { history } from "app-store-v2/index"
import useCollection from "areas/collection/hoc/use-collection"
import IntersectionWrapper from "components/common/intersection-wrapper"
import { isTestMode } from "helpers/application"
import { logCurrentPageInCollectionStats } from "helpers/collection"
import scrollToElement from "helpers/scroll-to-element"
import useScrollRestoration from "helpers/use-scroll-restoration"
import withLanding from "hoc/landing/with-landing"

import LandingElement from "./landing-element"
import LandingLoading from "./loaders"

const Landing = () => {
  const { hash } = useLocation()
  const { data, isRefetching } = useCollection()

  useScrollRestoration()

  useEffect(() => {
    if (data) {
      logCurrentPageInCollectionStats(data.url_path)

      if (data.unique_event_id) {
        window.cache_free_ssr_data.unique_event_ids.landing =
          data.unique_event_id
      }

      if (!isTestMode) {
        dataLayer.push({
          event: "view-landing",
          landingPath: data.url_path,
          landingTitle: data.title,
          viewLandingUniqueEventId:
            window.cache_free_ssr_data.unique_event_ids.landing,
        })
      }
    }
  }, []) // Runs only once

  useLayoutEffect(() => {
    if (data && data.elements && hash) {
      const element = data.elements.find((el) => `#${el.anchor_name}` === hash)

      if (element) {
        scrollToElement(hash)
        history.replace({ hash: null })
      }
    }
  }, [data, hash])

  return (
    <>
      <Helmet encodeSpecialCharacters={false}>
        <title>{data.title}</title>
        {/* eslint-disable sort-keys, react/jsx-sort-props, react/no-unknown-property */}
        {(data.preloadable_images || []).map((image, i) =>
          (image.srcSet || []).map((srcset) => (
            <link
              key={`landing-image-preload-${image.id || i}-${srcset.media}`}
              rel="preload"
              fetchPriority="high"
              as="image"
              imageSrcSet={srcset.srcSet}
              imageSizes={srcset.sizes}
              media={srcset.media}
            />
          ))
        )}
        {data.seo_faq?.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: data.seo_faq.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.rich_answer
                    ? marked.parseInline(faq.rich_answer)
                    : faq.answer,
                },
              })),
            })}
          </script>
        )}
        {/* eslint-enable sort-keys, react/jsx-sort-props, react/no-unknown-property */}
      </Helmet>
      {data.elements_list.map((el, index) => {
        const elId = el.anchor_name || el.id
        const landing_element = data.elements.find((elm) => elm.id === el.id)
        let content

        if (landing_element) {
          // Only render the first THREE elements
          if (index <= 2) {
            content = (
              <LandingElement
                el={landing_element}
                index={index}
                wrapTheChild={false}
              />
            )
          } else {
            // The other elements will be load/render when being in the view port
            content = (
              <IntersectionWrapper>
                <LandingElement
                  el={landing_element}
                  index={index}
                  wrapTheChild={false}
                />
              </IntersectionWrapper>
            )
          }
        }

        return (
          <div id={elId} key={`landing-wrapper-${index}`}>
            {content}
          </div>
        )
      })}
      {isRefetching && <LandingLoading partial />}
    </>
  )
}

export default withLanding(Landing)
